import React, { useState, useRef, useEffect } from 'react'
import { COUNTRIES } from '../data/countries'

const MAX_OPTIONS = 10

function filterCountries(query: string): string[] {
  const q = query.trim().toLowerCase()
  if (!q) return COUNTRIES.slice(0, MAX_OPTIONS)
  return COUNTRIES.filter(
    c => c.toLowerCase().startsWith(q) || c.toLowerCase().includes(q)
  ).slice(0, MAX_OPTIONS)
}

export interface CountrySelectProps {
  value: string
  onChange: (value: string) => void
  className?: string
  placeholder?: string
  id?: string
  required?: boolean
  'aria-label'?: string
}

const CountrySelect: React.FC<CountrySelectProps> = ({
  value,
  onChange,
  className = '',
  placeholder = 'e.g. India',
  id,
  required,
  'aria-label': ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState(value)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setInputValue(value)
  }, [value])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const options = filterCountries(inputValue)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value
    setInputValue(v)
    onChange(v)
    setIsOpen(true)
  }

  const handleFocus = () => {
    setIsOpen(true)
  }

  const handleSelect = (country: string) => {
    setInputValue(country)
    onChange(country)
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} className="relative">
      <input
        id={id}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        required={required}
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoComplete="off"
        className={className}
      />
      {isOpen && (
        <ul
          className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg py-1 text-sm"
          role="listbox"
        >
          {options.length > 0 ? (
            options.map(country => (
              <li
                key={country}
                role="option"
                aria-selected={value === country}
                onClick={() => handleSelect(country)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleSelect(country)
                  }
                }}
                className="px-4 py-2.5 cursor-pointer hover:bg-primary-50 hover:text-primary-900 text-gray-900"
              >
                {country}
              </li>
            ))
          ) : (
            <li className="px-4 py-2.5 text-gray-500">No matching country</li>
          )}
        </ul>
      )}
    </div>
  )
}

export default CountrySelect
